import {
  EventType,
  HistoryVisibility,
  ICreateRoomStateEvent,
  JoinRule,
  MatrixClient,
  Preset,
} from 'matrix-js-sdk/src/matrix';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCalendarSyncContext } from 'contexts/CalendarSyncProvider/CalendarSyncProvider';
import useFeatureFlagContext from 'contexts/FeatureFlagProvider/useFeatureFlagContext';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useRoomAccessInitialState from 'hooks/roomCreation/useRoomAccessInitialState';
import useRoomCreationPowerLevels, {
  mergeRoomCreationPowerLevelOverrides,
} from 'hooks/roomCreation/useRoomCreationPowerLevels';
import useRoomManagersInitialState from 'hooks/roomCreation/useRoomManagersInitialState';
import useCreateRoom from 'hooks/useCreateRoom/useCreateRoom';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import { calculatePermissions, WorkspacePermissionName } from 'hooks/useWorkspacePermissions/utils';
import { AuthTokens } from 'modules/CalendarView/CalendarSync/CalendarSyncModal/authHelpers';
import {
  addNewEventToCalendar,
  deleteEventFromCalendar,
  getPrimaryCalendarInformation,
} from 'modules/CalendarView/CalendarSync/CalendarSyncModal/calendarHelpers';
import { storageUtils } from 'modules/CalendarView/CalendarSync/CalendarSyncModal/utils';
import { getIsMatrixError, MatrixErrorEnum } from 'modules/CreateNewRoomModal/CreateRoomStepModal/AliasField/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { FeatureFlag } from 'utils/Constants';
import logger from 'utils/logging/faro';
import { buildMeetingSpaceChatChildInitialState, getViaServers, sendChildRoomParentStates } from 'utils/matrixUtils';
import { logAndSendToSentry } from 'utils/utils';

import { buildMeetingInitialStates, encryptionState, getWorkspaceMeetingPowerLevelOverrides } from './utils';

type CreateMeetingProps = {
  title: string;
  alias?: string;
  isWaitingListEnabled: boolean;
  isPrivateRoom?: boolean;
  description?: string;
  customInitialStates?: ICreateRoomStateEvent[];
  preset?: Preset;
  isBreakout?: boolean;
};

interface CreatedMeeting {
  space: { room_id: string };
  chat: { room_id: string };
  calendarInfo: { eventId?: string; primaryCalendarId?: string; token?: AuthTokens };
}

type RejectToast = {
  variant: 'error' | 'warning';
  title: string;
  description?: string;
};

interface UseCreateMeetingReturn {
  createMeeting: (props: CreateMeetingProps) => Promise<CreatedMeeting | undefined>;
  isLoading: boolean;
}

const getRequiredMeetingPermission = (props: CreateMeetingProps): WorkspacePermissionName => {
  if (props.isPrivateRoom) return 'createPrivateMeeting';
  if (props.isWaitingListEnabled) return 'createWaitingListMeeting';
  if (props.preset === Preset.PrivateChat) return 'createScheduledMeeting';
  return 'createInstantMeeting';
};

const getLinkFailureRejectToast = (
  props: CreateMeetingProps,
  error: unknown,
  t: (key: string) => string,
  matrixClient: MatrixClient,
  workspaceId: string
): RejectToast => {
  const permissions = calculatePermissions(matrixClient, workspaceId);
  const requiredPermission = getRequiredMeetingPermission(props);

  if ((getIsMatrixError(error) && error.errcode === MatrixErrorEnum.M_FORBIDDEN) || !permissions[requiredPermission]) {
    return {
      variant: 'warning',
      title: t('workspaces.no_permission'),
      description: t('workspaces.no_permission_description'),
    };
  }

  return { variant: 'error', title: t('something_went_wrong_try_again_later') };
};

const useCreateMeeting = (): UseCreateMeetingReturn => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { workspaceId } = useWorkspacesContext();
  const { pushToast } = useToast();
  const pushNoPermissionToast = useNoPermissionToast();
  const { createRoom } = useCreateRoom();
  const { isEnabled } = useFeatureFlagContext();
  const { mainCalendarInfo, accountCalendars } = useCalendarSyncContext();
  const { ownerPowerLevel: meetingOwnerPowerLevel } = useRoomCreationPowerLevels('meeting');
  const roomManagersInitialState = useRoomManagersInitialState(workspaceId);
  const teamRoomAccessInitialState = useRoomAccessInitialState('team');
  const knockRoomAccessInitialState = useRoomAccessInitialState('knock');

  const isRoomE2EEEnabled = isEnabled(FeatureFlag.ROOM_E2EE);
  const [isLoading, setIsLoading] = useState(false);

  const hasCreatePermission = useCallback(
    (props: CreateMeetingProps): boolean => {
      if (!workspaceId || props.isBreakout) return true;

      const permissions = calculatePermissions(matrixClient, workspaceId);
      const requiredPermission = getRequiredMeetingPermission(props);

      if (!permissions[requiredPermission]) {
        pushNoPermissionToast();
        return false;
      }

      return true;
    },
    [matrixClient, pushNoPermissionToast, workspaceId]
  );

  const createMeeting = useCallback(
    async (props: CreateMeetingProps): Promise<CreatedMeeting | undefined> => {
      if (isLoading) return undefined;
      if (!hasCreatePermission(props)) return undefined;

      const {
        title,
        alias,
        isWaitingListEnabled,
        isPrivateRoom,
        description,
        customInitialStates = [],
        preset,
        isBreakout = false,
      } = props;

      let space: { room_id: string } | undefined;
      let chatRoom: { room_id: string } | undefined;
      let eventId: string | undefined;

      const meetingStates = buildMeetingInitialStates({
        isPrivateRoom: !!isPrivateRoom,
        workspaceId,
        restrictedToRoomId: null,
        matrixClient,
        isRoomE2EEEnabled,
        isWaitingListEnabled,
      });
      const accessStates: ICreateRoomStateEvent[] = isBreakout
        ? []
        : workspaceId
        ? [isWaitingListEnabled ? knockRoomAccessInitialState : teamRoomAccessInitialState]
        : [];
      const managersStates = workspaceId && roomManagersInitialState ? [roomManagersInitialState] : [];
      const initialStates = [...customInitialStates, ...meetingStates, ...managersStates, ...accessStates];

      const { primaryCalendarId, token, isTokenExpired } = getPrimaryCalendarInformation(
        accountCalendars,
        mainCalendarInfo
      );

      const workspaceOverrides = workspaceId ? getWorkspaceMeetingPowerLevelOverrides() : undefined;
      const powerLevelOverrides = mergeRoomCreationPowerLevelOverrides(workspaceOverrides);
      const ownerPowerLevel = meetingOwnerPowerLevel;

      setIsLoading(true);

      try {
        const chatPreliminaryStates: ICreateRoomStateEvent[] = [
          {
            type: EventType.RoomHistoryVisibility,
            state_key: '',
            content: { history_visibility: HistoryVisibility.Invited },
          },
          ...managersStates,
          ...(isRoomE2EEEnabled ? [encryptionState] : []),
        ];

        chatRoom = await createRoom({
          title,
          customInitialStates: chatPreliminaryStates,
          powerLevelOverrides,
          includeBot: !!workspaceId && !isBreakout,
          ownerPowerLevel,
        });

        const spaceInitialStates = [...initialStates, buildMeetingSpaceChatChildInitialState(chatRoom.room_id)];

        space = await createRoom({
          customCreationContent: { type: 'm.space' },
          customInitialStates: spaceInitialStates,
          title,
          alias,
          description,
          preset: isPrivateRoom ? Preset.PrivateChat : preset,
          powerLevelOverrides,
          includeBot: !!workspaceId && !isBreakout,
          ownerPowerLevel,
        });

        await sendChildRoomParentStates(matrixClient, chatRoom.room_id, space.room_id, JoinRule.Restricted);

        if (primaryCalendarId && token && !isTokenExpired && !isBreakout) {
          logger.info('Adding event to primary calendar', { primaryCalendarId, roomId: space.room_id });
          const provider = storageUtils.getProviderFromToken(token);
          const meetingUrl = `${window.location.origin}/rooms/${alias}`;
          const { data, error } = await addNewEventToCalendar(
            provider,
            primaryCalendarId,
            {
              title,
              description: t('meeting_room_description', { url: meetingUrl }),
              location: meetingUrl,
              provider,
              calendarId: primaryCalendarId,
              startDate: new Date().toISOString(),
              endDate: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
            },
            token.access_token,
            space.room_id
          );

          if (data?.id) {
            eventId = data.id;
            logger.info('Calendar event created', { eventId, roomId: space.room_id });
          } else {
            logger.error('Failed to create calendar event', { roomId: space.room_id, error });
            pushToast({ variant: 'warning', title: t('event_creation_error') });
          }
        }

        if (workspaceId && !isBreakout) {
          const viaServers = getViaServers(workspaceId, matrixClient);
          try {
            await matrixClient.sendStateEvent(
              workspaceId,
              EventType.SpaceChild,
              { via: viaServers, suggested: true },
              space.room_id
            );
          } catch (error) {
            try {
              await matrixClient.leave(space.room_id);
            } catch (leaveError) {
              logAndSendToSentry('Failed to leave orphan meeting space after workspace link failure', {
                error: leaveError,
                roomId: space.room_id,
              });
            }

            try {
              await matrixClient.leave(chatRoom.room_id);
            } catch (leaveError) {
              logAndSendToSentry('Failed to leave orphan meeting chat after workspace link failure', {
                error: leaveError,
                roomId: chatRoom.room_id,
              });
            }

            if (eventId && primaryCalendarId && token) {
              deleteEventFromCalendar(
                storageUtils.getProviderFromToken(token),
                primaryCalendarId,
                eventId,
                token.access_token
              );
            }

            pushToast(getLinkFailureRejectToast(props, error, t, matrixClient, workspaceId));
            return undefined;
          }
        }

        return { space, chat: chatRoom, calendarInfo: { eventId, primaryCalendarId, token } };
      } catch (error) {
        logger.error('Cannot create matrix room', error);
        if (chatRoom) matrixClient.leave(chatRoom.room_id);
        if (space) matrixClient.leave(space.room_id);

        if (eventId && primaryCalendarId && token) {
          logger.info('Deleting calendar event due to room creation failure', { eventId, roomId: space?.room_id });
          deleteEventFromCalendar(
            storageUtils.getProviderFromToken(token),
            primaryCalendarId,
            eventId,
            token.access_token
          );
        }

        pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [
      accountCalendars,
      createRoom,
      hasCreatePermission,
      isLoading,
      isRoomE2EEEnabled,
      mainCalendarInfo,
      matrixClient,
      meetingOwnerPowerLevel,
      pushToast,
      roomManagersInitialState,
      t,
      knockRoomAccessInitialState,
      teamRoomAccessInitialState,
      workspaceId,
    ]
  );

  return {
    createMeeting,
    isLoading,
  };
};

export default useCreateMeeting;
